<template>
  <b-modal
    v-model="showModal"
    size="lg"
    centered
    scrollable
    hide-footer
    title="ส่ง WhatsApp Template"
    @hidden="reset"
  >
    <div class="small text-muted mb-3">
      เกิน 24 ชม. นับจากข้อความล่าสุดของลูกค้าแล้ว WhatsApp
      ให้ส่งได้เฉพาะข้อความที่ใช้ Template ที่ได้รับอนุมัติ
      เมื่อลูกค้าตอบกลับจะพิมพ์ข้อความปกติได้อีกครั้ง
    </div>

    <div v-if="isLoading" class="text-center my-4">
      <b-spinner class="align-middle"></b-spinner>
    </div>
    <div v-else-if="errorMessage" class="text-danger my-3">
      <font-awesome-icon class="mr-1" icon="exclamation-circle" />
      {{ errorMessage }}
      <b-button size="sm" variant="link" @click="getTemplates"
        >ลองใหม่</b-button
      >
    </div>

    <!-- เลือก template -->
    <div v-else-if="!selected">
      <b-form-input
        v-model="search"
        size="sm"
        placeholder="ค้นหาชื่อหรือข้อความ Template"
        class="mb-2"
      ></b-form-input>
      <div v-if="!filteredTemplates.length" class="text-muted my-3">
        ไม่พบ Template ที่ได้รับอนุมัติ ให้หัวหน้างานสร้างได้ที่หน้าตั้งค่า
        WhatsApp Template
      </div>
      <div
        v-for="template in filteredTemplates"
        :key="template.id || template.name"
        class="template-item"
        :class="{ disabled: !!template.unsupported }"
        @click="selectTemplate(template)"
      >
        <div class="d-flex align-items-center justify-content-between">
          <div class="font-weight-bold text-break">{{ template.name }}</div>
          <div class="text-nowrap ml-2">
            <b-badge
              :variant="categoryInfo(template.category).variant"
              class="mr-1"
              >{{ categoryInfo(template.category).label }}</b-badge
            >
            <b-badge variant="light">{{ template.language }}</b-badge>
          </div>
        </div>
        <div class="small text-muted template-body">
          {{ bodyText(template) }}
        </div>
        <div v-if="template.unsupported" class="small text-danger">
          {{ template.unsupported }}
        </div>
      </div>
    </div>

    <!-- กรอกตัวแปร + preview -->
    <div v-else>
      <b-button
        size="sm"
        variant="link"
        class="px-0 mb-2"
        @click="selected = null"
      >
        <font-awesome-icon class="mr-1" icon="chevron-left" />เลือก Template
        อื่น
      </b-button>
      <div class="d-flex align-items-center mb-2">
        <div class="font-weight-bold mr-2 text-break">{{ selected.name }}</div>
        <b-badge :variant="categoryInfo(selected.category).variant">{{
          categoryInfo(selected.category).label
        }}</b-badge>
      </div>
      <b-alert
        :show="selected.category === 'MARKETING'"
        variant="warning"
        class="small py-2"
      >
        {{ categoryInfo("MARKETING").note }}
      </b-alert>

      <b-row>
        <b-col md="6">
          <div v-if="headerValues.length" class="mb-2">
            <div class="small font-weight-bold mb-1">ตัวแปรในหัวข้อ</div>
            <b-input-group
              v-for="(value, index) in headerValues"
              :key="'h' + index"
              size="sm"
              class="mb-1"
              :prepend="`{{${index + 1}}}`"
            >
              <b-form-input v-model="headerValues[index]"></b-form-input>
            </b-input-group>
          </div>
          <div v-if="bodyValues.length" class="mb-2">
            <div class="small font-weight-bold mb-1">ตัวแปรในข้อความ</div>
            <b-input-group
              v-for="(value, index) in bodyValues"
              :key="'b' + index"
              size="sm"
              class="mb-1"
              :prepend="`{{${index + 1}}}`"
            >
              <b-form-input
                v-model="bodyValues[index]"
                :placeholder="bodyExample(index)"
              ></b-form-input>
              <b-input-group-append v-if="customerName">
                <b-button
                  variant="outline-secondary"
                  title="ใส่ชื่อลูกค้า"
                  @click="$set(bodyValues, index, customerName)"
                  >ชื่อลูกค้า</b-button
                >
              </b-input-group-append>
            </b-input-group>
          </div>
          <div
            v-if="!headerValues.length && !bodyValues.length"
            class="small text-muted mb-2"
          >
            Template นี้ไม่มีตัวแปร ส่งได้ทันที
          </div>
        </b-col>
        <b-col md="6">
          <div class="small font-weight-bold mb-1">ตัวอย่างที่ลูกค้าจะเห็น</div>
          <div class="wa-preview">
            <div class="wa-bubble">
              <div v-if="preview.header" class="font-weight-bold mb-1">
                {{ preview.header }}
              </div>
              <div class="wa-body">{{ preview.body }}</div>
              <div v-if="preview.footer" class="wa-footer">
                {{ preview.footer }}
              </div>
            </div>
            <div
              v-for="(button, index) in preview.buttons"
              :key="index"
              class="wa-button"
            >
              {{ button.text }}
            </div>
          </div>
        </b-col>
      </b-row>

      <div v-if="missingVariable" class="small text-danger mt-2">
        กรุณากรอกตัวแปรให้ครบ
      </div>
      <div class="d-flex justify-content-end mt-3">
        <b-button variant="light" class="mr-2" @click="showModal = false"
          >ยกเลิก</b-button
        >
        <b-button
          variant="main-primary"
          :disabled="missingVariable || isSending"
          @click="confirm"
          >ส่ง Template</b-button
        >
      </div>
    </div>
  </b-modal>
</template>

<script>
import {
  CATEGORY_INFO,
  countVariables,
  findComponent,
  renderText,
  renderTemplateText,
  unsupportedReason
} from "@/helpers/whatsappTemplate";

export default {
  name: "ChatBoxModalWhatsappTemplate",
  props: {
    chatRoomId: {
      type: String,
      required: false
    },
    customerName: {
      type: String,
      required: false,
      default: ""
    }
  },
  data() {
    return {
      showModal: false,
      isLoading: false,
      isSending: false,
      errorMessage: "",
      templates: [],
      search: "",
      selected: null,
      headerValues: [],
      bodyValues: []
    };
  },
  computed: {
    filteredTemplates() {
      const keyword = this.search.trim().toLowerCase();
      return this.templates.filter(
        t =>
          !keyword ||
          t.name.toLowerCase().includes(keyword) ||
          this.bodyText(t)
            .toLowerCase()
            .includes(keyword)
      );
    },
    missingVariable() {
      return [...this.headerValues, ...this.bodyValues].some(
        v => !v || !v.trim()
      );
    },
    preview() {
      if (!this.selected) return {};
      const header = findComponent(this.selected, "HEADER");
      const footer = findComponent(this.selected, "FOOTER");
      const buttons = findComponent(this.selected, "BUTTONS");
      return {
        header: header ? renderText(header.text, this.headerValues) : "",
        body: renderText(this.bodyText(this.selected), this.bodyValues),
        footer: footer ? footer.text : "",
        buttons: buttons ? buttons.buttons || [] : []
      };
    }
  },
  methods: {
    show() {
      this.showModal = true;
      this.getTemplates();
    },
    hide() {
      this.showModal = false;
    },
    reset() {
      this.selected = null;
      this.search = "";
      this.isSending = false;
    },
    categoryInfo(category) {
      return (
        CATEGORY_INFO[category] || {
          label: category || "-",
          variant: "secondary"
        }
      );
    },
    bodyText(template) {
      const body = findComponent(template, "BODY");
      return body ? body.text || "" : "";
    },
    bodyExample(index) {
      const body = findComponent(this.selected, "BODY");
      const example = body && body.example;
      const values = example && (example.bodyText || example.body_text);
      return values && values[0] && values[0][index]
        ? `เช่น ${values[0][index]}`
        : "";
    },
    async getTemplates() {
      this.isLoading = true;
      this.errorMessage = "";
      try {
        const response = await fetch(
          `${this.$chatBaseUrl}/WhatsappTemplate/list/${this.chatRoomId}`,
          { method: "GET", headers: new Headers(this.$headersChat) }
        );
        const result = await response.json();
        if (result.result == 1) {
          this.templates = (result.detail || []).map(t => ({
            ...t,
            unsupported: unsupportedReason(t)
          }));
        } else {
          this.templates = [];
          this.errorMessage = result.message || "ดึงรายการ Template ไม่สำเร็จ";
        }
      } catch (error) {
        this.templates = [];
        this.errorMessage = "ดึงรายการ Template ไม่สำเร็จ";
      }
      this.isLoading = false;
    },
    selectTemplate(template) {
      if (template.unsupported) return;
      const header = findComponent(template, "HEADER");
      this.headerValues = Array(countVariables(header ? header.text : "")).fill(
        ""
      );
      this.bodyValues = Array(countVariables(this.bodyText(template))).fill("");
      this.selected = template;
    },
    confirm() {
      if (this.missingVariable || !this.selected) return;
      this.isSending = true;
      this.$emit("confirm", {
        name: this.selected.name,
        language: this.selected.language,
        category: this.selected.category,
        headerParameters: this.headerValues.map(v => v.trim()),
        bodyParameters: this.bodyValues.map(v => v.trim()),
        text: renderTemplateText(
          this.selected,
          this.headerValues,
          this.bodyValues
        )
      });
      this.showModal = false;
    }
  }
};
</script>

<style lang="scss" scoped>
.template-item {
  border: 1px solid #e9e9ef;
  border-radius: 6px;
  padding: 8px 12px;
  margin-bottom: 8px;
  cursor: pointer;

  &:hover {
    background-color: #f7f7f9;
  }

  &.disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
}

.template-body {
  white-space: pre-line;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.wa-preview {
  background-color: #efeae2;
  border-radius: 8px;
  padding: 12px;
}

.wa-bubble {
  background-color: white;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 14px;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.1);
}

.wa-body {
  white-space: pre-line;
  word-break: break-word;
}

.wa-footer {
  color: #8696a0;
  font-size: 12px;
  margin-top: 4px;
}

.wa-button {
  background-color: white;
  border-radius: 8px;
  margin-top: 4px;
  padding: 6px;
  text-align: center;
  color: #027eb5;
  font-size: 14px;
}
</style>
